import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import PriceGauge from './components/PriceGauge';
import ComparableSales from './components/ComparableSales';
import MarketTrends from './components/MarketTrends';
import PriceBreakdown from './components/PriceBreakdown';
import AIMarketInsights from './components/AIMarketInsights';

const mockUser = { id: 1, name: 'Sarah Mitchell', email: 'sarah@email.com', role: 'user' };

const FairPriceAnalysis = () => {
  const navigate = useNavigate();
  const [propertyUrl, setPropertyUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyze = () => {
    if (!propertyUrl?.trim()) return;
    setIsAnalyzing(true);
    setTimeout(() => setIsAnalyzing(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} onLogout={() => navigate('/authentication')} />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 text-sm">
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              Back to Home
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)' }}>
                <Icon name="Target" size={20} color="#00D4FF" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Fair Price Assessment</h1>
            </div>
            <p className="text-muted-foreground">AI-powered analysis to determine if a property is fairly priced based on market data and comparable sales.</p>
          </div>

          <div className="bg-card rounded-xl border border-border p-6 mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Analyse a Property</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={propertyUrl}
                onChange={e => setPropertyUrl(e?.target?.value)}
                placeholder="Paste Domain or realestate.com.au listing URL..."
                className="flex-1 px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
              />
              <button
                onClick={handleAnalyze}
                disabled={isAnalyzing || !propertyUrl?.trim()}
                className="px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000', boxShadow: '0 0 20px rgba(0,212,255,0.3)' }}
              >
                {isAnalyzing ? 'Analysing...' : 'Analyse Price'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Or use the sample analysis below to explore the feature.</p>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <Icon name="Info" size={18} color="#00D4FF" />
            <div>
              <p className="text-sm font-medium text-foreground">Sample Analysis: 42 Harbour View Terrace, Mosman NSW 2088</p>
              <p className="text-xs text-muted-foreground">Listing price: $2,800,000 — 4 bed, 3 bath, 2 parking</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Listing Price', value: '$2,800,000', icon: 'DollarSign', color: '#00D4FF' },
              { label: 'AI Fair Value', value: '$2,750,000', icon: 'Target', color: '#00FF88' },
              { label: 'Price Difference', value: '+1.8%', icon: 'TrendingUp', color: '#FFAA00' },
              { label: 'Days on Market', value: '47 days', icon: 'Clock', color: '#AA88FF' },
            ]?.map((m, i) => (
              <div key={i} className="bg-card rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={m?.icon} size={16} color={m?.color} />
                  <span className="text-xs text-muted-foreground">{m?.label}</span>
                </div>
                <p className="text-lg font-bold" style={{ color: m?.color }}>{m?.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <PriceGauge verdict="fair" confidence={87} />
            <PriceBreakdown />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <MarketTrends />
            <ComparableSales />
          </div>

          <AIMarketInsights />

          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.15)' }}>
                <Icon name="Sparkles" size={16} color="#00D4FF" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">AI Price Summary</h3>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-4">
              This Mosman property is <span className="text-green-400 font-medium">fairly priced</span> at $2,800,000. Our AI analysis of 47 comparable sales in the past 6 months indicates a fair market value of approximately $2,750,000–$2,850,000.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              The 47 days on market is slightly above the suburb median of 32 days. Consider negotiating toward the lower end of the range ($2,700,000–$2,750,000) given the extended marketing period.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={() => navigate('/offer-range-guidance')} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}>Get Offer Range →</button>
              <button onClick={() => navigate('/risk-assessment')} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(255,68,68,0.1)', color: '#FF6666', border: '1px solid rgba(255,68,68,0.3)' }}>Check Risks →</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FairPriceAnalysis;
